import { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Upload, Button, message, Select, Form } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { translate } from "../../locale";
import AbTestServices from "../../services/AbTestServices";

const { Option } = Select;

const AbTestUploader = ({
  visible,
  onCancel,
  onSuccess,
  record,
  title = translate("Cargar usuarios"),
}) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [groupType, setGroupType] = useState(null);
  const [fileList, setFileList] = useState([]);

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    beforeUpload: (file) => {
      const isValidType =
        file.type === "text/csv" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";

      if (!isValidType) {
        message.error(translate("Solo se permiten archivos CSV o Excel"));
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(translate("El archivo debe ser menor a 10MB"));
        return false;
      }

      return false; // Prevent auto upload
    },
    onChange: (info) => {
      if (info.fileList.length > 0) {
        const file = info.fileList[0].originFileObj;
        setFileList(info.fileList.slice(-1));
        setSelectedFile(file);
      } else {
        setFileList([]);
        setSelectedFile(null);
      }
    },
  };

  const uploadFileToBackend = async (file) => {
    setUploading(true);

    try {
      const body = {
        abtest_id: record.id,
        group_type: groupType,
        file_name: file.name,
        file: file,
      };

      const response = await AbTestServices.addManager(body);

      if (response.status >= 200 && response.status < 300) {
        message.success(translate("Archivo procesado exitosamente"));
        onSuccess();
        handleClose();
      } else {
        message.error(
          response.data?.message || translate("Error al procesar el archivo")
        );
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error(
        `${translate("Error al subir el archivo")}: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedFile || !groupType) {
      message.error(
        translate("Debe seleccionar un archivo y un tipo de grupo")
      );
      return;
    }
    await uploadFileToBackend(selectedFile);
  };

  const handleClose = () => {
    setFileList([]);
    setSelectedFile(null);
    setGroupType(null);
    onCancel();
  };

  const isFormValid = selectedFile && groupType;

  return (
    <Modal
      open={visible}
      title={title}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {translate("Cancelar")}
        </Button>,
        <Button
          key="accept"
          type="primary"
          onClick={handleAccept}
          disabled={!isFormValid || uploading}
          loading={uploading}
        >
          {uploading ? translate("Subiendo...") : translate("Aceptar")}
        </Button>,
      ]}
    >
      <div className="mb-4">
        <p>
          <strong>{translate("AB Test")}: </strong>
          {record?.name}
        </p>
      </div>

      <Form layout="vertical">
        <Form.Item label={translate("Tipo de grupo")} required className="mb-4">
          <Select
            placeholder={translate("Seleccione...")}
            value={groupType}
            onChange={setGroupType}
            disabled={uploading}
          >
            <Option value="test">{translate("Test")}</Option>
            <Option value="control">{translate("Control")}</Option>
          </Select>
        </Form.Item>

        <Form.Item label={translate("Archivo")} required>
          <Upload.Dragger
            {...uploadProps}
            disabled={uploading}
            fileList={fileList}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              {selectedFile
                ? `${translate("Archivo seleccionado")}: ${selectedFile.name}`
                : translate("Haz clic o arrastra un archivo aquí")}
            </p>
            <p className="ant-upload-hint">
              {translate(
                "Solo archivos CSV o Excel (.xlsx, .xls), máximo 10MB"
              )}
            </p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

AbTestUploader.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  record: PropTypes.object.isRequired,
  title: PropTypes.string,
};

export default AbTestUploader;
